import clsx from "clsx";
import {
  cloneElement,
  isValidElement,
  ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { TooltipPin } from "@/assets/icons/ds";
import { FCC } from "@/types";

import {
  getHintTooltipPlacement,
  HintTooltipAlign,
  HintTooltipPlacement,
} from "./getHintTooltipPlacement";

interface HintTooltipProps {
  /** Explains why the wrapped control is unavailable. Plain text in the design. */
  content: ReactNode;
  /**
   * Preferred edge of the trigger. The bubble is then shifted so it stays
   * inside the viewport — needed on phone and iPad, where the app shell's
   * overflow-x-clip would otherwise cut a 240px bubble that hangs off the card.
   */
  align?: HintTooltipAlign;
  className?: string;
  dataTestid?: string;
}

/**
 * A dark bubble with a pin toward the trigger, opened on hover/focus and toggled
 * by tap on touch devices. Prefers opening above (design-system default) and
 * flips below when there is not enough room. Rendered `position:fixed` so
 * ancestor overflow cannot clip it.
 */
export const HintTooltip: FCC<HintTooltipProps> = ({
  content,
  align = "left",
  className,
  children,
  dataTestid,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipId = useId();
  const [isVisible, setIsVisible] = useState(false);
  const [placement, setPlacement] = useState<HintTooltipPlacement | null>(null);
  // True while a focusable child holds keyboard/pointer focus. When set, the
  // onClick toggle is suppressed — focus already opened the tooltip and a
  // click on the same element must not immediately close it again.
  const openedByFocusRef = useRef(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useLayoutEffect(() => {
    if (!isVisible) {
      setPlacement(null);

      return;
    }

    const updatePlacement = () => {
      const trigger = containerRef.current?.getBoundingClientRect();
      if (!trigger) {
        return;
      }
      setPlacement(
        getHintTooltipPlacement({
          trigger,
          viewportWidth: window.visualViewport?.width ?? window.innerWidth,
          viewportHeight: window.visualViewport?.height ?? window.innerHeight,
          align,
        }),
      );
    };

    updatePlacement();
    window.addEventListener("scroll", updatePlacement, true);
    window.addEventListener("resize", updatePlacement);
    window.visualViewport?.addEventListener("resize", updatePlacement);

    return () => {
      window.removeEventListener("scroll", updatePlacement, true);
      window.removeEventListener("resize", updatePlacement);
      window.visualViewport?.removeEventListener("resize", updatePlacement);
    };
  }, [isVisible, align]);

  return (
    <div
      className={clsx("relative flex", className)}
      ref={containerRef}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => {
        if (!openedByFocusRef.current) {
          setIsVisible(false);
        }
      }}
      onFocus={() => {
        openedByFocusRef.current = true;
        setIsVisible(true);
      }}
      onBlur={() => {
        openedByFocusRef.current = false;
        setIsVisible(false);
      }}
    >
      {/* `min-w-0 w-full`: lets a truncated child (TruncatedText) shrink. */}
      <div
        className="w-full min-w-0"
        onClick={() => {
          // When a focusable child (e.g. button) is clicked, the browser fires
          // onFocus before onClick, so the tooltip is already open via
          // openedByFocusRef. Toggling here would close it immediately — skip.
          // For non-focusable triggers (span, div) focus never fires, so the
          // toggle is the only way to open/close.
          if (!openedByFocusRef.current) {
            setIsVisible((visible) => !visible);
          }
        }}
      >
        {/* Point the trigger at the bubble so a screen reader reads the reason
            out when focus lands on it, not just on hover. */}
        {isValidElement(children)
          ? cloneElement(children, { "aria-describedby": tooltipId } as never)
          : children}
      </div>
      {isVisible && placement && (
        <div
          id={tooltipId}
          data-testid={dataTestid}
          className={clsx(
            "fixed z-30 flex items-start",
            placement.side === "top" ? "flex-col-reverse" : "flex-col",
          )}
          data-align={align}
          data-side={placement.side}
          style={{
            top: placement.top,
            bottom: placement.bottom,
            left: placement.left,
            width: placement.width,
          }}
          role="tooltip"
        >
          <div style={{ marginLeft: placement.pinOffset }}>
            <TooltipPin
              className={clsx(
                "h-2 w-6 shrink-0 text-surface-primary",
                placement.side === "top" && "rotate-180",
              )}
            />
          </div>
          {/* A `div`, not a `p` - callers (e.g. the disabled-reason list in
              NetworkResetDisabledTooltip/EsimDisabledActionsTooltip) nest a
              `<ul>` in content, which is invalid inside a `<p>`. */}
          {/* `whitespace-normal`: the bubble is a fixed 240px, so its text has
              to wrap even when it renders inside something that sets
              `white-space: nowrap` - a table cell, for one. */}
          <div className="w-full whitespace-normal break-words rounded-md bg-surface-primary p-4 text-body-sm leading-md text-foreground-primary-inverse">
            {content}
          </div>
        </div>
      )}
    </div>
  );
};
